import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useContext, useMemo } from 'react';

import { CustomStylesCtx, useTextStyle } from '@webapp/common/components/custom-styles';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';

import css from './sem-diff.css';

const SemDiffGroup: FC<{
    questionId: number;
    answerId: number;
    group: GroupMachineActor;
    borderColor: string;
    textStyle: CSSProperties;
}> = ({ answerId, borderColor, group: groupActor, textStyle, questionId }) => {
    const group = useSelector(groupActor, (g) => g.context);
    const responseActor = useSelector(groupActor, (r) => r.context.responses[answerId]);
    const response = useSelector(responseActor, (r) => r.context);
    const selected = (response?.value || false) as boolean;

    const handleSelect = useCallback(
        (value: boolean) => () => {
            groupActor.send({ type: 'CHANGE_RESPONSE_VALUE', value, answerId });
        },
        [questionId, responseActor, answerId],
    );

    return (
        <div
            className={cn(css.item, { [css.selected]: selected })}
            key={group.id}
            style={
                selected
                    ? {
                          borderColor,
                          ...textStyle,
                      }
                    : textStyle
            }
            onClick={handleSelect(!selected)}
        >
            {group.name}
        </div>
    );
};

const SemDiffAnswer: FC<{
    id: number;
    questionId: number;
    answerStyle: CSSProperties;
    textStyle: CSSProperties;
    value1: any;
    valid: any;
    labelStyle: CSSProperties;
    borderColor: string;
    value2: any;
    groups: Array<GroupMachineActor>;
    value3: any;
}> = ({ questionId, answerStyle, borderColor, groups, id, labelStyle, textStyle, valid, value1, value2, value3 }) => (
    <div className={cn(CssPageBlock.ANSWER, css.answer)} style={answerStyle}>
        <div className={css.name} style={textStyle} {...setHtml(value1)} />
        <div
            className={cn(css.field, {
                [css.field_invalid]: valid === false,
            })}
        >
            <div className={cn(CssUiComponent.LABEL, css.label)} style={labelStyle} {...setHtml(value2)} />
            <div className={css.scale}>
                {groups.map((group) => (
                    <SemDiffGroup
                        questionId={questionId}
                        answerId={id}
                        borderColor={borderColor}
                        group={group}
                        key={group.id}
                        textStyle={textStyle}
                    />
                ))}
            </div>
            <div className={cn(CssUiComponent.LABEL, css.label)} style={labelStyle} {...setHtml(value3)} />
        </div>
    </div>
);

export const SemDiff: VFC<SurveyQuestion> = ({ question: { id: questionId, answersActors, groupsActors } }) => {
    const { answerStyle } = useTextStyle();
    const { text: textStyle } = useContext(CustomStylesCtx);
    const computedFontSize = Math.max(parseInt(`${textStyle.fontSize}`) * 0.75, 12);

    const labelStyle: CSSProperties = useMemo(
        () => ({
            ...textStyle,
            fontSize: isNaN(computedFontSize) ? textStyle.fontSize : `${computedFontSize}px`,
        }),
        [computedFontSize, textStyle],
    );

    return (
        <div className={cn(CssQuestionType.QUESTION, CssQuestionType.SEM_DIFF, css.diff)}>
            {answersActors.map((answerActor) => {
                const {
                    id,
                    valid,
                    value: [value1, value2, value3],
                } = answerActor.getSnapshot().context;
                return (
                    <SemDiffAnswer
                        questionId={questionId}
                        answerStyle={answerStyle}
                        borderColor={answerStyle.color}
                        groups={groupsActors}
                        id={id}
                        key={id}
                        labelStyle={labelStyle}
                        textStyle={textStyle}
                        valid={valid}
                        value1={value1}
                        value2={value2}
                        value3={value3}
                    />
                );
            })}
        </div>
    );
};
SemDiff.displayName = 'SemDiff';
